'use client'

import { LineChevronDownSmall } from '@qr-front/icons/line-chevron-down-small'
import { LineChevronUpSmall } from '@qr-front/icons/line-chevron-up-small'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import * as Popover from '@radix-ui/react-popover'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { limitChars } from '@/utils/limitChars'

import { OpeningHoursTab } from './TabNavigation'

type TimePickerPopoverProps = {
	value: string
	disabled?: boolean
	onChange: (value: string) => void
	selectedTab: OpeningHoursTab
}

const parseDate = (value: string): Date => {
	const parsed = new Date(value)
	return isNaN(parsed.getTime()) ? new Date() : parsed
}

export const TimePicker = ({
	value,
	disabled,
	onChange,
	selectedTab,
}: TimePickerPopoverProps) => {
	const t = useTranslations()
	const [initialDate, setInitialDate] = useState<Date>(() => parseDate(value))
	const [modifiedDate, setModifiedDate] = useState<Date>(new Date(initialDate))
	const [isOpen, setIsOpen] = useState<boolean>(false)

	useEffect(() => {
		const parsed = new Date(value)
		if (!isNaN(parsed.getTime())) {
			setInitialDate(parsed)
		}
	}, [value])

	const minutes = Array.from({ length: 12 }, (_, i) => i * 5)

	const AM_TEXT = limitChars(
		t('generator__content_form_section__opening_hours__tabs__am'),
		3,
	)
	const PM_TEXT = limitChars(
		t('generator__content_form_section__opening_hours__tabs__pm'),
		3,
	)

	const hours =
		selectedTab === 'am-pm'
			? Array.from({ length: 12 }, (_, i) => i + 1)
			: Array.from({ length: 24 }, (_, i) => i)

	const formatTime = (d: Date): string => {
		const h = d.getHours()
		const m = d.getMinutes().toString().padStart(2, '0')

		if (selectedTab === 'am-pm') {
			const hour12 = (h % 12 || 12).toString().padStart(2, '0')
			const ampm = h >= 12 ? PM_TEXT : AM_TEXT
			return `${hour12}:${m} ${ampm}`
		}

		return `${h.toString().padStart(2, '0')}:${m}`
	}

	const handleTimeChange = (type: 'hour' | 'minute' | 'ampm', val: string) => {
		const newDate = new Date(modifiedDate)

		if (type === 'hour') {
			let hour = parseInt(val, 10)

			if (selectedTab === 'am-pm') {
				const isPM = newDate.getHours() >= 12
				hour = hour % 12
				hour = isPM ? hour + 12 : hour
			}

			newDate.setHours(hour)
		}

		if (type === 'minute') {
			newDate.setMinutes(parseInt(val, 10))
		}

		if (type === 'ampm') {
			const currentHour = newDate.getHours()
			const isCurrentlyPM = currentHour >= 12

			if (val === 'AM' && isCurrentlyPM) {
				newDate.setHours(currentHour - 12)
			}
			if (val === 'PM' && !isCurrentlyPM) {
				newDate.setHours(currentHour + 12)
			}
		}

		setModifiedDate(newDate)
	}

	const handleSave = () => {
		setInitialDate(new Date(modifiedDate))
		setIsOpen(false)
		onChange(modifiedDate.toISOString())
	}

	const handleOpenChange = (open: boolean) => {
		if (open) {
			setModifiedDate(new Date(initialDate))
		}
		setIsOpen(open)
	}

	const getDisplayHour = (date: Date) => {
		const h = date.getHours()
		return selectedTab === 'am-pm' ? h % 12 || 12 : h
	}

	return (
		<Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
			<Popover.Trigger asChild>
				<button
					type="button"
					disabled={disabled}
					className={cn(
						'type-body-small flex w-[115px] items-center justify-between rounded-8 p-2 text-left ring-1 ring-border-grey tablet:w-40 tablet:px-4',
						disabled
							? 'pointer-events-none font-medium text-grey'
							: 'bg-white font-medium text-dark-grey',
					)}
				>
					{formatTime(initialDate)}
					{isOpen ? (
						<LineChevronUpSmall className="ml-2 size-4" />
					) : (
						<LineChevronDownSmall className="ml-2 size-4" />
					)}
				</button>
			</Popover.Trigger>

			<Popover.Content className="z-[5] my-1 w-40 max-w-40 rounded-8 bg-white px-2 py-2 shadow-generator">
				<div
					className={cn(
						'grid bg-white',
						selectedTab === 'am-pm' ? '-ml-[4.5px] grid-cols-3' : 'grid-cols-2',
					)}
				>
					{/* Hours */}
					<div
						className={cn(
							'hide-scrollbar mb-1 flex max-h-[217px] flex-col gap-1 overflow-y-scroll',
							selectedTab === '24h' ? 'pr-1' : 'px-1',
						)}
					>
						{hours.map((hour) => (
							<button
								key={hour}
								type="button"
								onClick={() => handleTimeChange('hour', hour.toString())}
								className={cn(
									'type-body-small h-8 min-h-8 w-auto px-3 py-1 hover:bg-light-grey-70',
									getVersionForClone({
										base: 'rounded-4',
										clone1: 'rounded-full',
									}),
									getDisplayHour(modifiedDate) === hour
										? 'bg-purple-8 text-blue'
										: 'bg-white text-black',
								)}
							>
								{hour.toString().padStart(2, '0')}
							</button>
						))}
					</div>

					{/* Minutes */}
					<div
						className={cn(
							'hide-scrollbar mb-1 flex max-h-[217px] flex-col gap-1 overflow-y-scroll border-l-[0.5px] border-border-grey-50',
							selectedTab === '24h' ? 'pl-1' : 'px-1',
						)}
					>
						{minutes.map((minute) => (
							<button
								key={minute}
								type="button"
								onClick={() => handleTimeChange('minute', minute.toString())}
								className={cn(
									'type-body-small h-8 min-h-8 w-auto px-3 py-1 hover:bg-light-grey-70',
									getVersionForClone({
										base: 'rounded-4',
										clone1: 'rounded-full',
									}),
									modifiedDate.getMinutes() === minute
										? 'bg-purple-8 text-blue'
										: 'bg-white text-black',
								)}
							>
								{minute.toString().padStart(2, '0')}
							</button>
						))}
					</div>

					{/* AM/PM */}
					{selectedTab === 'am-pm' && (
						<div className="hide-scrollbar mb-1 flex flex-col gap-1 border-l-[0.5px] border-border-grey-50 px-1">
							{['AM', 'PM'].map((el) => (
								<button
									key={el}
									type="button"
									onClick={() => handleTimeChange('ampm', el)}
									className={cn(
										'type-body-small h-8 w-11.5 px-3 py-1 hover:bg-light-grey-70',
										getVersionForClone({
											base: 'rounded-4',
											clone1: 'rounded-full',
										}),
										(el === 'AM' && modifiedDate.getHours() < 12) ||
											(el === 'PM' && modifiedDate.getHours() >= 12)
											? 'bg-purple-8 text-blue'
											: 'bg-white text-black',
									)}
								>
									{el === 'AM' ? AM_TEXT : PM_TEXT}
								</button>
							))}
						</div>
					)}
				</div>

				{/* Save Button */}
				<div className="flex justify-center border-t-[0.5px] border-t-border-grey-50 pt-2">
					<button
						type="button"
						onClick={handleSave}
						className={cn(
							'type-body-small h-8 w-full bg-blue px-3 py-1 text-white transition-colors duration-200 hover:bg-blue-hover',
							getVersionForClone({
								base: 'rounded-4',
								clone1: 'rounded-full',
							}),
						)}
					>
						{t('ui__generic__save')}
					</button>
				</div>
			</Popover.Content>
		</Popover.Root>
	)
}
